<template>
    <transition name="channel-qr-fade">
        <div v-if="value" class="channel-qr-overlay" @click.self="close">
            <div class="channel-qr-modal" role="dialog" aria-modal="true" aria-labelledby="channel-qr-title">
                <div class="channel-qr-head">
                    <div>
                        <div id="channel-qr-title" class="channel-qr-title">QR-код канала</div>
                        <div class="channel-qr-sub">
                            Отсканируйте код, чтобы подключить {{ channel?.displayName || 'канал' }}.
                        </div>
                    </div>
                    <button type="button" class="channel-qr-x" aria-label="Закрыть" @click="close">&times;</button>
                </div>
                <div class="channel-qr-body">
                    <div v-if="channel" class="channel-qr-meta">
                        <span class="channel-qr-meta__ico" :style="{ background: channel.bg }">
                            <v-img
                                v-if="channel.image"
                                :src="channel.image"
                                class="channel-qr-meta__img"
                            />
                            <v-icon v-else size="18" color="#fff">{{ channel.mdiIcon }}</v-icon>
                        </span>
                        <div class="channel-qr-name">{{ channel.displayName }}</div>
                    </div>

                    <div v-if="status === 'authorized'" class="channel-qr-authorized">
                        <img
                            class="channel-qr-authorized__icon"
                            :src="require('@/assets/icons/success-status.svg')"
                            alt=""
                        />
                        <span>Канал подключён!</span>
                    </div>
                    <div v-else-if="status === 'password_needed'" class="channel-qr-password">
                        <span>
                            Требуется облачный пароль Telegram. Откройте
                            <a
                                class="channel-qr-link"
                                :href="channelSettingsHref"
                                target="_blank"
                                rel="noopener noreferrer"
                                @click="close"
                            >настройки канала</a>, чтобы завершить подключение.
                        </span>
                    </div>
                    <div v-else-if="status === 'unsupported'" class="channel-qr-unsupported">
                        <span>
                            Для этого канала QR-код недоступен. Откройте
                            <a
                                class="channel-qr-link"
                                :href="channelSettingsHref"
                                target="_blank"
                                rel="noopener noreferrer"
                                @click="close"
                            >настройки канала</a>
                            для подключения.
                        </span>
                    </div>
                    <div v-else-if="imageSrc" class="channel-qr-code">
                        <img :src="imageSrc" alt="QR-код канала" class="channel-qr-code__img" />
                    </div>
                    <div v-else class="channel-qr-loading">
                        <v-progress-circular indeterminate size="36" width="3" color="mainNew" />
                        <span class="channel-qr-loading__title">QR-код загружается...</span>
                        <span class="channel-qr-loading__hint">Загрузка может занять около минуты</span>
                    </div>

                    <div v-if="status !== 'authorized'" class="channel-qr-note">
                        <template v-if="isQrSetupUnavailable">
                            Чтобы подключить канал, нажмите ссылку «настройки канала» выше — откроется
                            страница проекта с настройками этого канала. После подключения канал войдёт
                            в каскад с тем же приоритетом.
                        </template>
                        <template v-else>
                            Откройте мессенджер на рабочем телефоне и отсканируйте QR-код. Приоритет канала
                            сохранится; после подключения он начнёт участвовать в каскаде.
                        </template>
                    </div>

                    <div v-if="canShare && imageSrc" class="channel-qr-actions">
                        <button type="button" class="channel-qr-share" :disabled="sharing" @click="shareQrCode">
                            Поделиться QR-кодом
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import api from '@root/src/api/api'
import {
    createQrFetchTimers,
    fetchChannelQrCode,
    getBotProviderFromProviderType,
} from '@/helpers/channels/fetchChannelQrCode'
import { PROVIDER_TYPES } from '@root/src/vars/general'

const QR_POLL_INTERVAL_MS = 5000

export default {
    name: 'StarterPackChannelQrModal',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        channel: {
            type: Object,
            default: null,
        },
        projectId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            sharing: false,
            imageSrc: null,
            status: null,
            fetchQrInterval: null,
            qrTimers: createQrFetchTimers(),
            isPollingRequestInFlight: false,
        }
    },
    computed: {
        canShare() {
            if (!this.channel?.providerType) return false
            return [
                PROVIDER_TYPES.whatsappBasis,
                PROVIDER_TYPES.whatsappApi,
                PROVIDER_TYPES.whatsappDh,
                PROVIDER_TYPES.telegramApi,
                PROVIDER_TYPES.telegramDh,
                PROVIDER_TYPES.maxBasis,
                PROVIDER_TYPES.maxDh,
            ].includes(this.channel.providerType)
        },
        project() {
            return this.$store.getters.projectById(this.projectId) || {}
        },
        channelSettingsHref() {
            if (!this.projectId || !this.channel?.connectorId) {
                return '#'
            }

            const url = new URL(`${window.location.origin}/project/${this.projectId}`)
            url.searchParams.set('openConnectorSidebar', String(this.channel.connectorId))
            return url.toString()
        },
        isQrSetupUnavailable() {
            return ['unsupported', 'password_needed'].includes(this.status)
        },
    },
    watch: {
        value(isOpen) {
            if (isOpen) {
                this.startQrPolling()
            } else {
                this.stopQrPolling()
                this.resetState()
            }
        },
        channel() {
            if (this.value) {
                this.startQrPolling()
            }
        },
    },
    beforeDestroy() {
        this.stopQrPolling()
    },
    methods: {
        close() {
            this.$emit('input', false)
        },
        resetState() {
            this.sharing = false
            this.imageSrc = null
            this.status = null
            this.qrTimers = createQrFetchTimers()
            this.isPollingRequestInFlight = false
        },
        startQrPolling() {
            this.stopQrPolling()
            this.resetState()
            void this.checkAndHandleQrStatus()
            this.fetchQrInterval = setInterval(() => {
                if (this.value) {
                    void this.checkAndHandleQrStatus()
                }
            }, QR_POLL_INTERVAL_MS)
        },
        stopQrPolling() {
            if (this.fetchQrInterval) {
                clearInterval(this.fetchQrInterval)
                this.fetchQrInterval = null
            }
        },
        shouldStopPolling() {
            return ['authorized', 'password_needed', 'unsupported'].includes(this.status)
        },
        async checkAndHandleQrStatus() {
            if (!this.channel?.connectorId || this.isPollingRequestInFlight || this.shouldStopPolling()) {
                return
            }

            this.isPollingRequestInFlight = true
            try {
                const result = await fetchChannelQrCode({
                    connectorId: this.channel.connectorId,
                    providerType: this.channel.providerType,
                    forcedProvider: this.channel.forcedProvider,
                    projectId: this.projectId,
                    timers: this.qrTimers,
                })

                if (result.throttled) {
                    return
                }

                this.status = result.status

                if (result.imageSrc) {
                    this.imageSrc = result.imageSrc
                }

                if (result.status === 'authorized') {
                    this.stopQrPolling()
                    this.$emit('authorized', this.channel.connectorId)
                    return
                }

                if (result.status === 'password_needed' || result.status === 'unsupported') {
                    this.stopQrPolling()
                }
            } finally {
                this.isPollingRequestInFlight = false
            }
        },
        async shareQrCode() {
            if (!this.channel?.connectorId || this.sharing) {
                return
            }

            this.sharing = true
            try {
                const res = await api.networkFixer.getNoAuthToken({ connectorId: this.channel.connectorId })
                if (res.error) {
                    this.$store.dispatch('callNotify', 'Ошибка при генерации ссылки')
                    return
                }

                const token = res.data.token
                const isChannelsRework = this.project?.isChannelsRework ? 'true' : 'false'
                const provider = getBotProviderFromProviderType(this.channel.providerType)
                const qrCodeUrl = `${window.location.origin}/qr/${this.channel.connectorId}?token=${token}&isChannelsRework=${isChannelsRework}&provider=${provider}`
                await navigator.clipboard.writeText(qrCodeUrl)
                window.open(qrCodeUrl, '_blank')
                this.$store.dispatch('callNotify', 'Ссылка скопирована в буфер обмена')
            } finally {
                this.sharing = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.channel-qr-overlay {
    position: fixed;
    inset: 0;
    z-index: 130;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(26, 26, 46, 0.46);
}

.channel-qr-modal {
    width: min(430px, 94vw);
    overflow: hidden;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 18px;
    box-shadow: 0 22px 70px rgba(26, 26, 46, 0.28);
}

.channel-qr-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 22px 22px 14px;
    border-bottom: 1px solid #eef1f6;
}

.channel-qr-title {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 19px;
    font-weight: 900;
    letter-spacing: -0.02em;
}

.channel-qr-sub {
    margin-top: 4px;
    color: $muted-new;
    font-size: 12.5px;
    line-height: 1.45;
}

.channel-qr-x {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    color: $muted-new;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    background: #f6f7fa;
    border: 1px solid $border-new;
    border-radius: 10px;
    transition: 0.15s;

    &:hover {
        color: $dark-new;
        background: #eef1f6;
    }
}

.channel-qr-body {
    padding: 20px 22px 22px;
}

.channel-qr-meta {
    display: flex;
    gap: 11px;
    align-items: center;
    padding: 12px;
    margin-bottom: 16px;
    background: #f7f9fc;
    border: 1px solid #eef1f6;
    border-radius: 13px;
}

.channel-qr-meta__ico {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    overflow: hidden;
    border-radius: 10px;
}

.channel-qr-meta__img {
    width: 100%;
    height: 100%;
}

.channel-qr-name {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 800;
}

.channel-qr-loading,
.channel-qr-authorized,
.channel-qr-password,
.channel-qr-unsupported {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    margin-bottom: 16px;
    color: $text-color-new;
    font-size: 13px;
    text-align: center;
}

.channel-qr-loading__title {
    font-weight: 600;
}

.channel-qr-loading__hint {
    color: $muted-new;
    font-size: 12px;
}

.channel-qr-authorized {
    flex-direction: row;
    gap: 8px;
    min-height: 120px;
    color: #16a957;
    font-weight: 600;
}

.channel-qr-authorized__icon {
    width: 18px;
    height: 18px;
}

.channel-qr-password,
.channel-qr-unsupported {
    padding: 0 12px;
    line-height: 1.5;
}

.channel-qr-link {
    color: $main-new;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
        color: darken($main-new, 8%);
    }
}

.channel-qr-code {
    width: 230px;
    height: 230px;
    padding: 16px;
    margin: 0 auto 16px;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 18px;
    box-shadow: 0 4px 16px rgba(26, 26, 46, 0.08);
}

.channel-qr-code__img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.channel-qr-note {
    margin-bottom: 14px;
    padding: 12px 14px;
    color: $text-color-new;
    font-size: 12.5px;
    line-height: 1.5;
    background: #fffbea;
    border: 1px solid #f3e6bb;
    border-radius: 12px;
}

.channel-qr-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}

.channel-qr-share {
    padding: 10px 14px;
    color: $main-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.02em;
    cursor: pointer;
    background: $bcg-card-new;
    border: 1.4px solid #d2d6f2;
    border-radius: 10px;
    transition: 0.15s;

    &:hover:not(:disabled) {
        background: #e2e5fb;
        border-color: #bdc3ee;
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
}

.channel-qr-fade-enter-active,
.channel-qr-fade-leave-active {
    transition: opacity 0.2s ease;
}

.channel-qr-fade-enter,
.channel-qr-fade-leave-to {
    opacity: 0;
}
</style>
